import {
    AlertTriangle,
    ArrowRight,
    BadgeIndianRupee,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    CircleAlert,
    Copy,
    Flag,
    Link2,
    LoaderCircle,
    MessageCircle,
    PencilLine,
    RotateCw,
    ShieldOff,
    Send,
    Share2,
    WalletCards,
    X,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FaCircleUser } from 'react-icons/fa6'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StaffPayslipDetailScreen.module.css'

const RUN_STATUS_CONFIG = {
    DRAFT: { label: 'Draft', color: '#E65100', bg: '#FFF3E0' },
    REVIEW: { label: 'In Review', color: '#1565C0', bg: '#E3F2FD' },
    FINALIZED: { label: 'Finalized', color: '#2E7D32', bg: '#E8F5E9' },
}

const ATTENDANCE_DISPUTE_TEMPLATES = [
    {
        componentName: 'Present Days',
        placeholder: 'I was present on [dates] but these are marked absent.',
    },
    {
        componentName: 'LWP Days',
        placeholder: 'Leave was approved but still deducted as LWP.',
    },
    {
        componentName: 'Late Count',
        placeholder: 'I was not late on [dates]. Please check in-time records.',
    },
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount, minimumFractionDigits = 0, maximumFractionDigits = 0) => {
    const value = Number(amount)
    if (!Number.isFinite(value)) return '—'
    return value.toLocaleString('en-IN', {
        minimumFractionDigits,
        maximumFractionDigits,
    })
}

const formatDate = (value) => {
    if (!value) return '—'
    const parsed = String(value).includes(' ') ? String(value).replace(' ', 'T') : String(value)
    const date = new Date(parsed)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

const formatSignedINR = (amount, isDeduction = false) => {
    const formatted = formatINR(amount, 2, 2)
    return `${isDeduction ? '−' : ''}₹${formatted}`
}

const canOverrideLineItem = (item, runStatus) => {
    if (!item) return false
    if (!['DRAFT', 'REVIEW'].includes(String(runStatus || '').toUpperCase())) return false
    if (String(item.componentCode || '').toUpperCase() === 'ADVANCE_EMI') return false
    if (!['EARNING', 'DEDUCTION'].includes(String(item.componentCategory || '').toUpperCase())) return false
    return Boolean(item.ledgerIdentifier)
}

const StaffPayslipDetailScreen = () => {
    const navigate = useNavigate()
    const { runIdentifier, staffIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [payslip, setPayslip] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [employerExpanded, setEmployerExpanded] = useState(false)
    const [selectedItem, setSelectedItem] = useState(null)
    const [newValue, setNewValue] = useState('')
    const [overrideReason, setOverrideReason] = useState('')
    const [isOverriding, setIsOverriding] = useState(false)
    const [valueError, setValueError] = useState(null)
    const [reasonError, setReasonError] = useState(null)
    const [shareData, setShareData] = useState(null)
    const [isGenerating, setIsGenerating] = useState(false)
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)
    const [isSending, setIsSending] = useState(false)
    const [deliveryResult, setDeliveryResult] = useState(null)
    const [deliveryIssue, setDeliveryIssue] = useState(null)
    const [disputeItem, setDisputeItem] = useState(null)
    const [disputeExpectedAmount, setDisputeExpectedAmount] = useState('')
    const [disputeReason, setDisputeReason] = useState('')
    const [disputeAmountError, setDisputeAmountError] = useState(null)
    const [disputeReasonError, setDisputeReasonError] = useState(null)
    const [isSubmittingDispute, setIsSubmittingDispute] = useState(false)
    const [disputeResult, setDisputeResult] = useState(null)

    const fetchPayslip = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setIsLoading(false)
            return
        }
        if (!runIdentifier || !staffIdentifier) {
            toast.error('Payslip context is missing.')
            navigate(-1)
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getStaffPayslipDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                    staffIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setPayslip(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load payslip.')
            navigate(-1)
        } catch {
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate, runIdentifier, staffIdentifier])

    useEffect(() => {
        fetchPayslip()
    }, [fetchPayslip])

    useEffect(() => {
        if (payslip?.isShareable && payslip?.shareToken) {
            setShareData({
                summaryIdentifier: payslip?.summaryIdentifier || null,
                staffName: payslip?.staffName || '',
                isShareable: true,
                shareToken: payslip.shareToken,
                shareableUrl: payslip.shareableUrl,
                shareTokenGeneratedOn: payslip.shareTokenGeneratedOn || null,
                isExisting: true,
                subdomain: payslip?.subdomain ?? null,
            })
            return
        }

        setShareData(null)
    }, [
        payslip?.isShareable,
        payslip?.shareToken,
        payslip?.shareableUrl,
        payslip?.shareTokenGeneratedOn,
        payslip?.summaryIdentifier,
        payslip?.staffName,
        payslip?.subdomain,
    ])

    const runStatusKey = String(payslip?.runStatus || 'DRAFT').toUpperCase()
    const runStatusMeta = RUN_STATUS_CONFIG[runStatusKey] || {
        label: runStatusKey,
        color: '#475569',
        bg: '#E2E8F0',
    }

    const earnings = payslip?.earnings || []
    const incentives = payslip?.incentives || []
    const deductions = payslip?.deductions || []
    const advanceDeductions = payslip?.advanceDeductions || []
    const statutoryDeductions = payslip?.statutoryDeductions || []
    const employerContributions = payslip?.employerContributions || []
    const summary = payslip?.summary || {}
    const attendance = payslip?.attendance || {}
    const revenue = payslip?.revenue || {}

    const mergedEmployerContributions = useMemo(() => {
        if (employerContributions.length > 0) return employerContributions

        const rows = []
        const employerPf = Number(payslip?.employerPfContribution || 0)
        const employerEsi = Number(payslip?.employerEsiContribution || 0)

        if (employerPf > 0) {
            rows.push({ componentName: 'Employer PF Contribution', computedValue: employerPf })
        }
        if (employerEsi > 0) {
            rows.push({ componentName: 'Employer ESI Contribution', computedValue: employerEsi })
        }

        return rows
    }, [employerContributions, payslip?.employerEsiContribution, payslip?.employerPfContribution])

    const totalEmployerCost = Number(
        summary?.totalEmployerCost ||
            Number(summary?.grossEarnings || 0) +
                Number(payslip?.employerPfContribution || 0) +
                Number(payslip?.employerEsiContribution || 0)
    )

    const allLineItems = useMemo(
        () => [
            ...earnings.map((item) => ({ ...item, componentCategory: item?.componentCategory || 'EARNING' })),
            ...incentives.map((item) => ({ ...item, componentCategory: item?.componentCategory || 'EARNING' })),
            ...deductions.map((item) => ({ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' })),
            ...advanceDeductions.map((item) => ({ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' })),
            ...statutoryDeductions.map((item) => ({ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' })),
        ],
        [advanceDeductions, deductions, earnings, incentives, statutoryDeductions]
    )

    const estimatedNetPayDelta = useMemo(() => {
        if (!selectedItem) return 0
        const parsedValue = Number.parseFloat(newValue)
        const targetValue = Number.isFinite(parsedValue) ? parsedValue : 0
        const currentValue = Number(selectedItem?.computedValue || 0)
        const delta = targetValue - currentValue
        const category = String(selectedItem?.componentCategory || '').toUpperCase()
        if (category === 'EARNING') return delta
        if (category === 'DEDUCTION') return -delta
        return 0
    }, [newValue, selectedItem])

    const estimatedNewNetPay = useMemo(() => {
        return Number(summary?.netPay || 0) + estimatedNetPayDelta
    }, [estimatedNetPayDelta, summary?.netPay])

    const disputeDelta = useMemo(() => {
        if (!disputeItem) return 0
        const expectedValue = Number.parseFloat(disputeExpectedAmount)
        if (!Number.isFinite(expectedValue)) return 0
        const currentValue = Number(disputeItem?.computedValue || 0)
        return expectedValue - currentValue
    }, [disputeExpectedAmount, disputeItem])

    const isAttendanceDispute = useMemo(() => {
        const componentName = String(disputeItem?.componentName || '').toLowerCase()
        return componentName.includes('days') || componentName.includes('late')
    }, [disputeItem?.componentName])

    const openOverrideSheet = (ledgerItem) => {
        if (!canOverrideLineItem(ledgerItem, runStatusKey)) {
            toast.info('This line item cannot be overridden.')
            return
        }
        setDisputeItem(null)
        setDisputeResult(null)
        setSelectedItem(ledgerItem)
        setNewValue(String(ledgerItem?.computedValue ?? '0'))
        setOverrideReason('')
        setValueError(null)
        setReasonError(null)
    }

    const closeOverrideSheet = () => {
        if (isOverriding) return
        setSelectedItem(null)
        setNewValue('')
        setOverrideReason('')
        setValueError(null)
        setReasonError(null)
    }

    const applyLocalOverrideUpdate = (overrideResponse) => {
        if (!overrideResponse?.ledgerIdentifier) return

        setPayslip((prev) => {
            if (!prev) return prev

            const patchLineItems = (rows = []) =>
                rows.map((item) => {
                    if (item?.ledgerIdentifier !== overrideResponse.ledgerIdentifier) return item

                    const originalFromItem = Number(item?.overrideOriginalValue || 0)
                    const preservedOriginal =
                        Number(overrideResponse?.overrideOriginalValue) ||
                        (originalFromItem > 0 ? originalFromItem : Number(overrideResponse?.previousValue || item?.computedValue || 0))

                    return {
                        ...item,
                        computedValue: Number(overrideResponse?.newValue ?? item?.computedValue ?? 0),
                        isOverridden: true,
                        overrideOriginalValue: preservedOriginal,
                        overrideReason: overrideResponse?.overrideReason || overrideReason.trim(),
                        overriddenBy: overrideResponse?.overriddenBy || item?.overriddenBy || '',
                    }
                })

            return {
                ...prev,
                earnings: patchLineItems(prev.earnings || []),
                incentives: patchLineItems(prev.incentives || []),
                deductions: patchLineItems(prev.deductions || []),
                advanceDeductions: patchLineItems(prev.advanceDeductions || []),
                statutoryDeductions: patchLineItems(prev.statutoryDeductions || []),
                summary: {
                    ...(prev.summary || {}),
                    ...(overrideResponse?.updatedStaffSummary || {}),
                },
                updatedRunTotals: {
                    ...(prev.updatedRunTotals || {}),
                    ...(overrideResponse?.updatedRunTotals || {}),
                },
            }
        })
    }

    const submitOverride = async () => {
        const parsedValue = Number.parseFloat(newValue)
        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
            setValueError('Enter a valid amount (0 or greater).')
            return
        }
        if (!overrideReason.trim()) {
            setReasonError('Provide a reason for this change.')
            return
        }
        if (!selectedItem?.ledgerIdentifier) {
            toast.error('Ledger context is missing.')
            return
        }

        setValueError(null)
        setReasonError(null)

        try {
            setIsOverriding(true)

            const response = await fetch(BASE_URL_API + 'overridePayrollLineItem', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    runIdentifier,
                    ledgerIdentifier: selectedItem.ledgerIdentifier,
                    newValue: parsedValue,
                    overrideReason: overrideReason.trim(),
                    overriddenBy: authCtx?.authIdentifier
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                const payload = respo.response || {}
                const netPayDelta = Number(payload?.updatedStaffSummary?.netPayDelta || 0)
                const direction = netPayDelta >= 0 ? '+' : '−'

                applyLocalOverrideUpdate(payload)

                toast.success(
                    `${payload?.componentName || selectedItem?.componentName || 'Line item'} updated. Net pay ${direction}₹${formatINR(
                        Math.abs(netPayDelta)
                    )}.`
                )
                closeOverrideSheet()
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if (String(respo?.message || '').toLowerCase().includes('overridereason')) {
                        setReasonError(respo.message)
                    } else {
                        toast.error(respo?.message || 'Override failed.')
                    }
                    break
                case 'VAL_002':
                    setValueError(respo?.message || 'newValue is required and must be 0 or greater.')
                    break
                case 'VAL_008':
                    toast.info('No change — value is the same as current.')
                    break
                case 'VAL_007':
                    toast.error(respo?.message || 'Ledger line item not found.')
                    closeOverrideSheet()
                    break
                case 'PAY_002':
                    toast.error(`Cannot override — run is ${respo?.response?.runStatus || 'FINALIZED'}.`)
                    closeOverrideSheet()
                    break
                case 'PAY_003':
                    toast.error(respo?.message || 'This line item cannot be overridden.')
                    closeOverrideSheet()
                    break
                case 'PAY_001':
                case 'SYS_002':
                default:
                    toast.error(respo?.message || 'Override failed.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsOverriding(false)
        }
    }

    const handleOverride = () => {
        const firstOverrideEligible = allLineItems.find((item) => canOverrideLineItem(item, runStatusKey))
        if (!firstOverrideEligible) {
            toast.info('No override-eligible line items found for this payslip.')
            return
        }
        openOverrideSheet(firstOverrideEligible)
    }

    const applyShareDataToPayslip = useCallback((payload) => {
        setPayslip((prev) => {
            if (!prev) return prev
            return {
                ...prev,
                isShareable: Boolean(payload?.isShareable),
                shareToken: payload?.shareToken || null,
                shareableUrl: payload?.shareableUrl || null,
                shareTokenGeneratedOn: payload?.shareTokenGeneratedOn || prev?.shareTokenGeneratedOn || null,
                subdomain:
                    payload?.subdomain !== undefined
                        ? payload?.subdomain
                        : prev?.subdomain !== undefined
                            ? prev?.subdomain
                            : null,
            }
        })
    }, [])

    const generateShareToken = useCallback(
        async (forceRegenerate = false) => {
            if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier || !staffIdentifier) {
                toast.error('Missing payslip context for sharing.')
                return
            }

            try {
                setIsGenerating(true)

                const response = await fetch(BASE_URL_API + 'generatePayslipShareToken', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore.storeIdentifier,
                        runIdentifier,
                        staffIdentifier,
                        forceRegenerate,
                    }),
                })

                const respo = await response.json()

                if (respo?.status === 'success') {
                    const payload = respo?.response || {}
                    setShareData(payload)
                    applyShareDataToPayslip(payload)

                    if (forceRegenerate) {
                        toast.success('Share link regenerated.')
                    } else if (!payload?.isExisting) {
                        toast.success('Share link generated.')
                    } else {
                        toast.info('Existing share link is active.')
                    }
                    return
                }

                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'VAL_007':
                    case 'SYS_002':
                    case 'VAL_001':
                        toast.error(respo?.message || 'Failed to generate share link.')
                        break
                    default:
                        toast.error(respo?.message || 'Failed to generate share link.')
                }
            } catch {
                toast.error('Network error.')
            } finally {
                setIsGenerating(false)
            }
        },
        [activeStore?.storeIdentifier, applyShareDataToPayslip, authCtx, runIdentifier, staffIdentifier]
    )

    const revokeShareToken = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier || !staffIdentifier) {
            toast.error('Missing payslip context for sharing.')
            return
        }

        try {
            setIsGenerating(true)

            const response = await fetch(BASE_URL_API + 'generatePayslipShareToken', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                    staffIdentifier,
                    isShareable: false,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                const payload = respo?.response || {}
                setShareData(null)
                setShowRevokeConfirm(false)
                applyShareDataToPayslip(payload)
                toast.info('Share link revoked.')
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_007':
                case 'SYS_002':
                case 'VAL_001':
                    toast.error(respo?.message || 'Failed to revoke share link.')
                    break
                default:
                    toast.error(respo?.message || 'Failed to revoke share link.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsGenerating(false)
        }
    }, [activeStore?.storeIdentifier, applyShareDataToPayslip, authCtx, runIdentifier, staffIdentifier])

    const handleGenerateShareLink = () => {
        generateShareToken(false)
    }

    const sendPayslipWhatsapp = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !runIdentifier || !staffIdentifier) {
            toast.error('Missing payslip context for WhatsApp send.')
            return
        }

        try {
            setIsSending(true)
            setDeliveryIssue(null)

            const response = await fetch(BASE_URL_API + 'sendPayslipOnWhatsapp', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    runIdentifier,
                    staffIdentifier,
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setDeliveryResult(respo.response || null)
                setDeliveryIssue(null)
                if (respo?.response?.shareableUrl) {
                    setShareData((prev) => ({
                        ...(prev || {}),
                        ...(respo.response || {}),
                        isShareable: true,
                        shareableUrl: respo.response.shareableUrl,
                    }))
                }
                toast.success(`Payslip sent to ${respo?.response?.staffName || payslip?.staffName || 'staff'} on WhatsApp.`)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setDeliveryResult(null)

            switch (respo?.errorCode) {
                case 'PAY_005':
                    setDeliveryIssue({
                        code: 'PAY_005',
                        message: respo?.message || 'Add a mobile number first.',
                        response: respo?.response || null,
                    })
                    toast.error(respo?.message || 'Add a mobile number first.')
                    break
                case 'WA_001':
                    setDeliveryIssue({
                        code: 'WA_001',
                        message: respo?.message || 'WhatsApp delivery failed.',
                        response: respo?.response || null,
                    })
                    toast.error(`WhatsApp delivery failed. ${respo?.response?.failureReason || ''}`.trim())
                    break
                case 'VAL_007':
                    toast.error('Payslip not found.')
                    break
                case 'PAY_001':
                    toast.error(respo?.message || 'Payroll run not found.')
                    break
                default:
                    toast.error(respo?.message || 'Send failed.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsSending(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, payslip?.staffName, runIdentifier, staffIdentifier])

    const copyShareLink = async () => {
        if (!shareData?.shareableUrl) return

        try {
            await navigator.clipboard.writeText(shareData.shareableUrl)
            toast.success('Share link copied.')
        } catch {
            toast.error('Unable to copy link.')
        }
    }

    const openStaffProfile = () => {
        navigate(`/payroll/staff-compensation/${staffIdentifier}`, {
            state: { staffIdentifier, staffName: payslip?.staffName || '', source: `/payroll/run/${runIdentifier}` },
        })
    }

    const closeDisputeSheet = useCallback(() => {
        if (isSubmittingDispute) return
        setDisputeItem(null)
        setDisputeExpectedAmount('')
        setDisputeReason('')
        setDisputeAmountError(null)
        setDisputeReasonError(null)
        setDisputeResult(null)
    }, [isSubmittingDispute])

    const openComponentDispute = useCallback((ledgerItem) => {
        if (!ledgerItem?.ledgerIdentifier) {
            toast.error('Ledger context missing for dispute.')
            return
        }

        closeOverrideSheet()
        setDisputeItem({
            ledgerIdentifier: ledgerItem.ledgerIdentifier,
            componentName: ledgerItem.componentName,
            computedValue: Number(ledgerItem.computedValue || 0),
            type: 'COMPONENT',
        })
        setDisputeExpectedAmount(String(Number(ledgerItem.computedValue || 0)))
        setDisputeReason('')
        setDisputeAmountError(null)
        setDisputeReasonError(null)
        setDisputeResult(null)
    }, [])

    const openOverallDispute = useCallback((componentName, currentValue, placeholder = '') => {
        closeOverrideSheet()
        setDisputeItem({
            ledgerIdentifier: null,
            componentName,
            computedValue: Number(currentValue || 0),
            type: 'OVERALL',
            placeholder,
        })
        setDisputeExpectedAmount(String(Number(currentValue || 0)))
        setDisputeReason('')
        setDisputeAmountError(null)
        setDisputeReasonError(null)
        setDisputeResult(null)
    }, [])

    const submitDispute = useCallback(async () => {
        const expectedValue = Number.parseFloat(disputeExpectedAmount)
        if (!Number.isFinite(expectedValue) || expectedValue < 0) {
            setDisputeAmountError('Enter a valid expected amount (0 or greater).')
            return
        }

        if (!disputeReason.trim()) {
            setDisputeReasonError('Provide a reason for this dispute.')
            return
        }

        if (!authCtx?.token || !activeStore?.storeIdentifier || !payslip?.summaryIdentifier || !disputeItem?.componentName) {
            toast.error('Dispute context is missing.')
            return
        }

        setDisputeAmountError(null)
        setDisputeReasonError(null)

        try {
            setIsSubmittingDispute(true)

            const body = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                summaryIdentifier: payslip.summaryIdentifier,
                disputedComponent: disputeItem.componentName,
                expectedAmount: expectedValue,
                disputeReason: disputeReason.trim(),
                raisedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
            }

            if (disputeItem?.ledgerIdentifier) {
                body.ledgerIdentifier = disputeItem.ledgerIdentifier
            } else {
                body.disputedAmount = Number(disputeItem?.computedValue || 0)
            }

            const response = await fetch(BASE_URL_API + 'raisePayslipDispute', {
                method: 'POST',
                body: JSON.stringify(body),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                setDisputeResult(respo.response || null)
                toast.success(respo?.message || 'Dispute raised successfully.')
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'PAY_006':
                    toast.warn('An active dispute already exists for this item.')
                    closeDisputeSheet()
                    break
                case 'VAL_001':
                    if (String(respo?.message || '').includes('disputeReason')) {
                        setDisputeReasonError(respo?.message || 'Provide a reason for this dispute.')
                    } else if (
                        String(respo?.message || '').includes('expectedAmount') ||
                        String(respo?.message || '').includes('disputedAmount')
                    ) {
                        setDisputeAmountError(respo?.message || 'Enter a valid expected amount.')
                    } else {
                        toast.error(respo?.message || 'Failed to raise dispute.')
                    }
                    break
                case 'VAL_007':
                    toast.error('Payslip or component not found.')
                    closeDisputeSheet()
                    break
                default:
                    toast.error(respo?.message || 'Failed to raise dispute.')
            }
        } catch {
            toast.error('Network error.')
        } finally {
            setIsSubmittingDispute(false)
        }
    }, [
        activeStore?.storeIdentifier,
        authCtx,
        closeDisputeSheet,
        disputeExpectedAmount,
        disputeItem,
        disputeReason,
        payslip?.summaryIdentifier,
    ])

    const handleApplyOverrideFromDispute = useCallback(() => {
        const ledgerIdentifier = disputeResult?.ledgerIdentifier || disputeItem?.ledgerIdentifier
        const matchedLedgerItem = allLineItems.find((item) => item?.ledgerIdentifier === ledgerIdentifier)

        if (matchedLedgerItem && canOverrideLineItem(matchedLedgerItem, runStatusKey)) {
            closeDisputeSheet()
            openOverrideSheet(matchedLedgerItem)
            return
        }

        closeDisputeSheet()
        handleOverride()
    }, [allLineItems, closeDisputeSheet, disputeItem?.ledgerIdentifier, disputeResult?.ledgerIdentifier, runStatusKey])

    if (!runIdentifier || !staffIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.inlineAlertWarn}>
                    <AlertTriangle size={16} />
                    <span>Run or staff context is missing. Open the payslip from a payroll run.</span>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>{payslip?.staffName || 'Payslip'}</h1>
                    <div className={styles.headerSubRow}>
                        <span>{payslip?.cycleLabel || 'Loading cycle...'}</span>
                        <span
                            className={styles.statusPill}
                            style={{ color: runStatusMeta.color, backgroundColor: runStatusMeta.bg }}
                        >
                            {runStatusMeta.label}
                        </span>
                    </div>
                </div>
                <div className={styles.actionBar}>
                    {runStatusKey !== 'FINALIZED' ? (
                        <button type="button" className={styles.primaryBtn} onClick={handleOverride}>
                            <PencilLine size={14} /> Override Line Item
                        </button>
                    ) : (
                        <>
                            <button type="button" className={styles.primaryBtn} onClick={handleGenerateShareLink} disabled={isGenerating}>
                                {isGenerating ? (
                                    <>
                                        <LoaderCircle size={14} className={styles.spinIcon} /> Generating...
                                    </>
                                ) : (
                                    <>
                                        <Share2 size={14} /> Share Payslip
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                onClick={sendPayslipWhatsapp}
                                disabled={isGenerating || isSending}
                            >
                                {isSending ? (
                                    <>
                                        <LoaderCircle size={14} className={styles.spinIcon} /> Sending...
                                    </>
                                ) : (
                                    <>
                                        <MessageCircle size={14} /> Send on WhatsApp
                                    </>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {isLoading ? (
                <div className={styles.loaderCard}>
                    <LoaderCircle size={20} className={styles.spinIcon} />
                    <span>Loading payslip...</span>
                </div>
            ) : !payslip ? (
                <div className={styles.inlineAlertWarn}>
                    <CircleAlert size={16} />
                    <span>Unable to load payslip data.</span>
                </div>
            ) : (
                <>
                    <section className={styles.staffCard}>
                        <div className={styles.staffIdentity}>
                            <FaCircleUser className={styles.avatarIcon} />
                            <div>
                                <strong>{payslip?.staffName}</strong>
                                <span>{payslip?.designationLabel || '—'}</span>
                            </div>
                        </div>

                        <div className={styles.metaGrid}>
                            <div className={styles.metaCell}>
                                <span>Templates</span>
                                <strong>
                                    {payslip?.salaryTemplate || '—'}
                                    {payslip?.incentiveTemplate ? ` · ${payslip.incentiveTemplate}` : ''}
                                </strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Cycle</span>
                                <strong>
                                    {formatDate(payslip?.cycleStartDate)} to {formatDate(payslip?.cycleEndDate)}
                                </strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Payment Date</span>
                                <strong>{formatDate(payslip?.paymentDate)}</strong>
                            </div>
                            <div className={styles.metaCell}>
                                <span>Summary ID</span>
                                <strong>{payslip?.summaryIdentifier || '—'}</strong>
                            </div>
                        </div>
                    </section>

                    <section className={styles.attendanceBar}>
                        <div className={styles.attendanceChip}>Present: {Number(attendance?.presentDays || 0).toFixed(1)}d</div>
                        <div className={styles.attendanceChip}>LWP: {Number(attendance?.lwpDays || 0).toFixed(1)}d</div>
                        <div className={styles.attendanceChip}>Leave: {Number(attendance?.approvedLeaveDays || 0).toFixed(1)}d</div>
                        <div className={styles.attendanceChip}>Late: {Number(attendance?.lateCount || 0)}</div>
                        <div className={styles.attendanceChipPrimary}>
                            Effective Pay Days: {Number(attendance?.effectivePayDays || 0).toFixed(1)}
                        </div>
                        {Number(revenue?.totalServiceRevenue || 0) > 0 ? (
                            <div className={styles.attendanceChipRevenue}>
                                Service Revenue: ₹{formatINR(revenue?.totalServiceRevenue)}
                            </div>
                        ) : null}
                    </section>

                    <SectionCard title="Earnings" icon={<BadgeIndianRupee size={15} />}>
                        {earnings.length === 0 ? (
                            <EmptyText text="No fixed earning rows for this payslip." />
                        ) : (
                            earnings.map((item) => (
                                <LineItemRow
                                    key={item.ledgerIdentifier || item.componentCode}
                                    item={{ ...item, componentCategory: item?.componentCategory || 'EARNING' }}
                                    runStatusKey={runStatusKey}
                                    onOverride={openOverrideSheet}
                                    onRaiseDispute={openComponentDispute}
                                />
                            ))
                        )}
                    </SectionCard>

                    {incentives.length > 0 ? (
                        <SectionCard title="Incentives" icon={<WalletCards size={15} />}>
                            {incentives.map((item) => (
                                <LineItemRow
                                    key={item.ledgerIdentifier || item.componentCode}
                                    item={{ ...item, componentCategory: item?.componentCategory || 'EARNING' }}
                                    runStatusKey={runStatusKey}
                                    onOverride={openOverrideSheet}
                                    onRaiseDispute={openComponentDispute}
                                />
                            ))}
                        </SectionCard>
                    ) : null}

                    <section className={styles.subtotalCard}>
                        <span>Gross Earnings</span>
                        <strong>₹{formatINR(summary?.grossEarnings, 2, 2)}</strong>
                    </section>

                    <SectionCard title="Deductions" icon={<Send size={15} />}>
                        {deductions.length === 0 && advanceDeductions.length === 0 && statutoryDeductions.length === 0 ? (
                            <EmptyText text="No deduction rows for this payslip." />
                        ) : (
                            <>
                                {deductions.map((item) => (
                                    <LineItemRow
                                        key={item.ledgerIdentifier || item.componentCode}
                                        item={{ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' }}
                                        isDeduction
                                        runStatusKey={runStatusKey}
                                        onOverride={openOverrideSheet}
                                        onRaiseDispute={openComponentDispute}
                                    />
                                ))}
                                {advanceDeductions.map((item) => (
                                    <LineItemRow
                                        key={item.ledgerIdentifier || item.componentCode}
                                        item={{ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' }}
                                        isDeduction
                                        runStatusKey={runStatusKey}
                                        onOverride={openOverrideSheet}
                                        onRaiseDispute={openComponentDispute}
                                    />
                                ))}
                                {statutoryDeductions.map((item) => (
                                    <LineItemRow
                                        key={item.ledgerIdentifier || item.componentCode}
                                        item={{ ...item, componentCategory: item?.componentCategory || 'DEDUCTION' }}
                                        isDeduction
                                        runStatusKey={runStatusKey}
                                        onOverride={openOverrideSheet}
                                        onRaiseDispute={openComponentDispute}
                                    />
                                ))}
                            </>
                        )}
                    </SectionCard>

                    <section className={`${styles.subtotalCard} ${styles.subtotalCardDanger}`}>
                        <span>Total Deductions</span>
                        <strong>−₹{formatINR(summary?.totalDeductions, 2, 2)}</strong>
                    </section>

                    <section className={styles.netPayCard}>
                        <div>
                            <span>Net Pay</span>
                            <p>After earnings, incentives, and all applicable deductions</p>
                        </div>
                        <strong>₹{formatINR(summary?.netPay, 2, 2)}</strong>
                    </section>

                    <section className={styles.disputeQuickCard}>
                        <div className={styles.disputeQuickHead}>
                            <strong>Raise Overall Dispute</strong>
                            <span>Log attendance or full payslip concerns for review.</span>
                        </div>
                        <div className={styles.disputeQuickActions}>
                            {ATTENDANCE_DISPUTE_TEMPLATES.map((template) => {
                                const currentValue =
                                    template.componentName === 'Present Days'
                                        ? Number(attendance?.presentDays || 0)
                                        : template.componentName === 'LWP Days'
                                            ? Number(attendance?.lwpDays || 0)
                                            : Number(attendance?.lateCount || 0)
                                return (
                                    <button
                                        key={template.componentName}
                                        type="button"
                                        className={styles.secondaryBtn}
                                        onClick={() => openOverallDispute(template.componentName, currentValue, template.placeholder)}
                                    >
                                        <Flag size={13} /> {template.componentName}
                                    </button>
                                )
                            })}
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                onClick={() =>
                                    openOverallDispute(
                                        'Overall Payslip',
                                        Number(summary?.netPay || 0),
                                        'The net payout seems incorrect. Please verify line items and attendance records.'
                                    )
                                }
                            >
                                <Flag size={13} /> Raise on Net Pay
                            </button>
                        </div>
                    </section>

                    <section className={styles.employerCard}>
                        <button
                            type="button"
                            className={styles.employerToggle}
                            onClick={() => setEmployerExpanded((prev) => !prev)}
                        >
                            <div>
                                <span>Employer Contributions</span>
                                <p>Informational only — not deducted from the staff payout</p>
                            </div>
                            {employerExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        {employerExpanded ? (
                            <div className={styles.employerBody}>
                                {mergedEmployerContributions.length > 0 ? (
                                    mergedEmployerContributions.map((item, index) => (
                                        <div className={styles.employerRow} key={`${item.componentName}-${index}`}>
                                            <span>{item.componentName}</span>
                                            <strong>₹{formatINR(item.computedValue, 2, 2)}</strong>
                                        </div>
                                    ))
                                ) : (
                                    <EmptyText text="No employer contribution rows for this payslip." compact />
                                )}
                                <div className={`${styles.employerRow} ${styles.employerRowTotal}`}>
                                    <span>Total Employer Cost</span>
                                    <strong>₹{formatINR(totalEmployerCost, 2, 2)}</strong>
                                </div>
                            </div>
                        ) : null}
                    </section>

                    {Number(summary?.advanceOutstandingAfter || 0) > 0 ? (
                        <section className={styles.inlineInfoCard}>
                            <WalletCards size={15} />
                            <span>
                                Advance outstanding after this deduction: ₹
                                {formatINR(summary?.advanceOutstandingAfter, 2, 2)}
                            </span>
                        </section>
                    ) : null}

                    {payslip?.isBelowMinimumWage ? (
                        <section className={styles.inlineAlertWarn}>
                            <AlertTriangle size={16} />
                            <span>Net pay is below the statutory minimum wage.</span>
                        </section>
                    ) : null}

                    {runStatusKey === 'FINALIZED' ? (
                        <section className={styles.shareCard}>
                            <div className={styles.shareHead}>
                                <div>
                                    <strong>Share Actions</strong>
                                    <span>
                                        {shareData?.isShareable && shareData?.shareableUrl
                                            ? 'Share link is available for copy or WhatsApp.'
                                            : 'Generate a share link to send finalized payslips.'}
                                    </span>
                                </div>
                            </div>

                            {shareData?.isShareable && shareData?.shareableUrl ? (
                                <div className={styles.shareUrlCard}>
                                    <span>Share URL</span>
                                    <strong title={shareData?.shareableUrl}>{shareData?.shareableUrl}</strong>
                                    {shareData?.shareTokenGeneratedOn ? (
                                        <small>Generated: {formatDate(shareData?.shareTokenGeneratedOn)}</small>
                                    ) : null}
                                </div>
                            ) : null}

                            {shareData?.isShareable && shareData?.subdomain === null ? (
                                <div className={styles.shareWarningCard}>
                                    <AlertTriangle size={14} />
                                    <span>
                                        Your salon profile does not have a staff portal subdomain yet. Fallback domain URL is being used.
                                    </span>
                                </div>
                            ) : null}

                            <div className={styles.shareActions}>
                                {!shareData?.shareableUrl ? (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={handleGenerateShareLink}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <Share2 size={14} /> Generate Share Link
                                                </>
                                            )}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={copyShareLink}
                                            disabled={isGenerating}
                                        >
                                            <Copy size={14} /> Copy Link
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={sendPayslipWhatsapp}
                                            disabled={isGenerating || isSending}
                                        >
                                            {isSending ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <MessageCircle size={14} /> WhatsApp
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => generateShareToken(true)}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Regenerating...
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCw size={14} /> Regenerate
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.dangerBtn}
                                            onClick={() => setShowRevokeConfirm(true)}
                                            disabled={isGenerating}
                                        >
                                            <ShieldOff size={14} /> Revoke Link
                                        </button>
                                    </>
                                )}
                            </div>

                            {deliveryResult ? (
                                <div className={styles.sentConfirmation}>
                                    <div className={styles.sentIconWrap}>
                                        <CheckCircle2 size={18} />
                                    </div>
                                    <div className={styles.sentCopy}>
                                        <strong>Sent to {deliveryResult?.recipientPhone || 'recipient'}</strong>
                                        <span>{formatDate(deliveryResult?.sentOn)}</span>
                                    </div>
                                </div>
                            ) : null}

                            {deliveryIssue?.code === 'PAY_005' ? (
                                <div className={styles.deliveryIssueCard}>
                                    <div>
                                        <strong>Add mobile number first</strong>
                                        <span>{deliveryIssue?.message}</span>
                                    </div>
                                    <button type="button" className={styles.secondaryBtn} onClick={openStaffProfile}>
                                        Open Staff Profile <ArrowRight size={14} />
                                    </button>
                                </div>
                            ) : null}

                            {deliveryIssue?.code === 'WA_001' ? (
                                <div className={styles.deliveryIssueCard}>
                                    <div>
                                        <strong>Delivery failed — try again</strong>
                                        <span>{deliveryIssue?.response?.failureReason || deliveryIssue?.message}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.secondaryBtn}
                                        onClick={sendPayslipWhatsapp}
                                        disabled={isSending}
                                    >
                                        {isSending ? (
                                            <>
                                                <LoaderCircle size={14} className={styles.spinIcon} /> Retrying...
                                            </>
                                        ) : (
                                            <>
                                                Retry Send <ArrowRight size={14} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : null}

                            {showRevokeConfirm ? (
                                <div className={styles.revokeConfirmCard}>
                                    <strong>Revoke public payslip link?</strong>
                                    <span>This immediately disables access for the current URL.</span>
                                    <div className={styles.revokeConfirmActions}>
                                        <button
                                            type="button"
                                            className={styles.secondaryBtn}
                                            onClick={() => setShowRevokeConfirm(false)}
                                            disabled={isGenerating}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.dangerBtn}
                                            onClick={revokeShareToken}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Revoking...
                                                </>
                                            ) : (
                                                <>
                                                    Revoke Link <ArrowRight size={14} />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </section>
                    ) : null}

                    {selectedItem ? (
                        <div className={styles.sheetBackdrop} role="presentation" onClick={closeOverrideSheet}>
                            <section
                                className={styles.sheetCard}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Override Line Item"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className={styles.sheetHead}>
                                    <div>
                                        <h3>Override Line Item</h3>
                                        <p>{selectedItem?.componentName || 'Line Item'}</p>
                                    </div>
                                    <button type="button" className={styles.sheetCloseBtn} onClick={closeOverrideSheet}>
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className={styles.sheetBlock}>
                                    <span>Current Value</span>
                                    <strong>₹{formatINR(selectedItem?.computedValue, 2, 2)}</strong>
                                    <p>{selectedItem?.computationBasis || '—'}</p>
                                </div>

                                {selectedItem?.isOverridden ? (
                                    <div className={styles.sheetEditedBadge}>
                                        Already overridden from ₹{formatINR(selectedItem?.overrideOriginalValue, 2, 2)}
                                    </div>
                                ) : null}

                                <div className={styles.sheetField}>
                                    <label htmlFor="overrideNewValue">New Value (₹)</label>
                                    <input
                                        id="overrideNewValue"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={newValue}
                                        onChange={(event) => {
                                            setNewValue(event.target.value)
                                            if (valueError) setValueError(null)
                                        }}
                                        placeholder="Enter new value"
                                    />
                                    {valueError ? <span className={styles.sheetError}>{valueError}</span> : null}
                                </div>

                                <div className={styles.sheetPreview}>
                                    <div>
                                        <span>New Net Pay</span>
                                        <strong>₹{formatINR(estimatedNewNetPay, 2, 2)}</strong>
                                    </div>
                                    <span
                                        className={styles.sheetPreviewDelta}
                                        style={{ color: estimatedNetPayDelta >= 0 ? '#2E7D32' : '#C62828' }}
                                    >
                                        {estimatedNetPayDelta >= 0 ? '+' : '−'}₹{formatINR(Math.abs(estimatedNetPayDelta), 2, 2)}
                                    </span>
                                </div>

                                <div className={styles.sheetField}>
                                    <label htmlFor="overrideReason">Reason for Override</label>
                                    <textarea
                                        id="overrideReason"
                                        rows={3}
                                        value={overrideReason}
                                        onChange={(event) => {
                                            setOverrideReason(event.target.value)
                                            if (reasonError) setReasonError(null)
                                        }}
                                        placeholder="e.g. 2 services missing from April 22 — confirmed with register"
                                    />
                                    {reasonError ? <span className={styles.sheetError}>{reasonError}</span> : null}
                                </div>

                                <div className={styles.sheetActions}>
                                    <button type="button" className={styles.secondaryBtn} onClick={closeOverrideSheet}>
                                        Cancel
                                    </button>
                                    <button type="button" className={styles.primaryBtn} onClick={submitOverride} disabled={isOverriding}>
                                        {isOverriding ? (
                                            <>
                                                <LoaderCircle size={14} className={styles.spinIcon} /> Applying...
                                            </>
                                        ) : (
                                            <>
                                                Apply Override <ArrowRight size={14} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </section>
                        </div>
                    ) : null}

                    {disputeItem ? (
                        <div className={styles.sheetBackdrop} role="presentation" onClick={closeDisputeSheet}>
                            <section
                                className={styles.sheetCard}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Raise Dispute"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className={styles.sheetHead}>
                                    <div>
                                        <h3>Raise a Dispute</h3>
                                        <p>{disputeItem?.componentName || 'Payslip Component'}</p>
                                    </div>
                                    <button type="button" className={styles.sheetCloseBtn} onClick={closeDisputeSheet}>
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className={styles.sheetBlock}>
                                    <span>Payslip Shows</span>
                                    <strong>
                                        {isAttendanceDispute
                                            ? `${Number(disputeItem?.computedValue || 0).toFixed(1)} days`
                                            : `₹${formatINR(disputeItem?.computedValue, 2, 2)}`}
                                    </strong>
                                </div>

                                <div className={styles.sheetField}>
                                    <label htmlFor="disputeExpectedAmount">Expected amount / value</label>
                                    <input
                                        id="disputeExpectedAmount"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={disputeExpectedAmount}
                                        onChange={(event) => {
                                            setDisputeExpectedAmount(event.target.value)
                                            if (disputeAmountError) setDisputeAmountError(null)
                                        }}
                                        placeholder="Enter expected value"
                                    />
                                    <span
                                        className={styles.disputeDeltaText}
                                        style={{ color: disputeDelta >= 0 ? '#2E7D32' : '#C62828' }}
                                    >
                                        Delta: {disputeDelta >= 0 ? '+' : '−'}
                                        {isAttendanceDispute
                                            ? `${Math.abs(disputeDelta).toFixed(1)} days`
                                            : `₹${formatINR(Math.abs(disputeDelta), 2, 2)}`}
                                    </span>
                                    {disputeAmountError ? <span className={styles.sheetError}>{disputeAmountError}</span> : null}
                                </div>

                                <div className={styles.sheetField}>
                                    <label htmlFor="disputeReason">Why is this incorrect?</label>
                                    <textarea
                                        id="disputeReason"
                                        rows={3}
                                        value={disputeReason}
                                        onChange={(event) => {
                                            setDisputeReason(event.target.value)
                                            if (disputeReasonError) setDisputeReasonError(null)
                                        }}
                                        placeholder={
                                            disputeItem?.placeholder ||
                                            'e.g. I served 42 clients this month, not 35 as shown. Please check session records for April 12-15.'
                                        }
                                    />
                                    {disputeReasonError ? <span className={styles.sheetError}>{disputeReasonError}</span> : null}
                                </div>

                                {disputeResult ? (
                                    <div className={styles.disputeSuccessCard}>
                                        <div className={styles.disputeSuccessHead}>
                                            <CheckCircle2 size={18} />
                                            <div>
                                                <strong>Dispute Raised</strong>
                                                <span>ID: {disputeResult?.disputeIdentifier || '—'}</span>
                                            </div>
                                        </div>

                                        <p
                                            className={
                                                disputeResult?.resolutionPath === 'OVERRIDE_AVAILABLE'
                                                    ? styles.disputeContextSuccess
                                                    : styles.disputeContextWarn
                                            }
                                        >
                                            {disputeResult?.contextNote ||
                                                (disputeResult?.resolutionPath === 'OVERRIDE_AVAILABLE'
                                                    ? 'Dispute recorded. You can apply correction override now.'
                                                    : 'Dispute recorded for next cycle correction.')}
                                        </p>

                                        <div className={styles.sheetActions}>
                                            {disputeResult?.resolutionPath === 'OVERRIDE_AVAILABLE' ? (
                                                <button
                                                    type="button"
                                                    className={styles.primaryBtn}
                                                    onClick={handleApplyOverrideFromDispute}
                                                >
                                                    Apply Override Now <ArrowRight size={14} />
                                                </button>
                                            ) : null}
                                            {disputeResult?.disputeIdentifier ? (
                                                <button
                                                    type="button"
                                                    className={styles.secondaryBtn}
                                                    onClick={() =>
                                                        navigate(
                                                            `/payroll/dispute/${disputeResult.disputeIdentifier}`,
                                                            { state: { dispute: disputeResult } }
                                                        )
                                                    }
                                                >
                                                    View Dispute <ArrowRight size={14} />
                                                </button>
                                            ) : null}
                                            <button type="button" className={styles.secondaryBtn} onClick={closeDisputeSheet}>
                                                Done
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.sheetActions}>
                                        <button type="button" className={styles.secondaryBtn} onClick={closeDisputeSheet}>
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={submitDispute}
                                            disabled={isSubmittingDispute}
                                        >
                                            {isSubmittingDispute ? (
                                                <>
                                                    <LoaderCircle size={14} className={styles.spinIcon} /> Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Dispute <ArrowRight size={14} />
                                                </>
                                            )}
                                        </button>
                                    </div>
                                )}
                            </section>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    )
}

const SectionCard = ({ title, icon, children }) => (
    <section className={styles.sectionCard}>
        <div className={styles.sectionHead}>
            <div className={styles.sectionTitleWrap}>
                {icon}
                <h2>{title}</h2>
            </div>
        </div>
        <div className={styles.lineList}>{children}</div>
    </section>
)

const EmptyText = ({ text, compact = false }) => (
    <p className={compact ? styles.emptyTextCompact : styles.emptyText}>{text}</p>
)

const LineItemRow = ({ item, isDeduction = false, runStatusKey, onOverride, onRaiseDispute }) => (
    <div className={styles.lineRow}>
        <div className={styles.lineLeft}>
            <div className={styles.labelRow}>
                <span className={styles.componentName}>{item?.componentName || 'Component'}</span>
                {item?.isOverridden ? (
                    <span className={styles.editedBadge}>EDITED</span>
                ) : null}
            </div>

            <p className={styles.computationBasis}>{item?.computationBasis || '—'}</p>

            {item?.isOverridden ? (
                <div className={styles.overrideDetail}>
                    <span className={styles.overrideOriginal}>
                        Original: ₹{formatINR(item?.overrideOriginalValue, 2, 2)}
                    </span>
                    <span className={styles.overrideReason}>Reason: {item?.overrideReason || '—'}</span>
                </div>
            ) : null}
        </div>

        <div className={styles.lineRight}>
            <span className={`${styles.componentValue} ${isDeduction ? styles.deductionValue : ''}`}>
                {formatSignedINR(item?.computedValue, isDeduction)}
            </span>
            <button type="button" className={styles.lineDisputeBtn} onClick={() => onRaiseDispute?.(item)}>
                <Flag size={12} /> Raise Dispute
            </button>
            {canOverrideLineItem(item, runStatusKey) ? (
                <button type="button" className={styles.lineOverrideBtn} onClick={() => onOverride?.(item)}>
                    <PencilLine size={12} /> Override
                </button>
            ) : null}
        </div>
    </div>
)

export default StaffPayslipDetailScreen